import {
  ConfirmationDocument,
  useRltConfirmationPdfDownloadMutation,
  useLazyRltConfirmationPdfPreviewQuery,
  useRltConfirmationUpdateStateMutation,
} from "@/services/backend/rlt/confirmations/api/confirmation-api";
import {
  useErrValidationFailedContext,
  useWriteValidationError,
} from "@/shared/lib/errors/err-validation-failed-context";
import { Button } from "@/shared/components/ui/button";
import t from "@/lang/lang";
import { Separator } from "@/shared/components/ui/separator";
import {
  DocumentNotReadyAlert,
  DocumentSheet,
  DocumentSheetActions,
  DocumentSheetActionsContent,
  DocumentSheetActionsDownload,
  DocumentSheetActionsHeader,
  DocumentSheetActionsSetImmutableMutation,
  DocumentSheetContent,
  DocumentSheetHeader,
  DocumentSheetImmutabilityWarning,
  DocumentSheetMain,
  DocumentSheetPreview,
  DocumentSheetTitle,
  DocumentSheetTrigger,
} from "@/shared/bsm/core/document/components/ui/document-sheet";
import { useAuth } from "@/shared/lib/authorization/auth-context";
import { Download } from "lucide-react";
import { useDownloadPdf } from "@/shared/lib/utilities/download-pdf";
import { useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { ReferenceNumber } from "@/services/backend/documents/components/reference-number";
import { Confirmation } from "@/services/backend/rlt/confirmations/types/confirmation";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import { DocumentState } from "@/services/backend/documents/types/state";

export function ConfirmationPdf({
  confirmation,
}: {
  confirmation: Confirmation;
}) {
  const previewRequest = {
    id: confirmation.id,
    document: ConfirmationDocument.All,
  };
  const [loadPreview, { data, isLoading, isFetching, error }] =
    useLazyRltConfirmationPdfPreviewQuery();

  const { write } = useErrValidationFailedContext();
  const { user } = useAuth();

  // The preview is rendered by the backend. It is loaded when the sheet
  // opens, not on every auto-save. Reopening shows the last one until the
  // fresh one arrives.
  const onOpenChange = (open: boolean) => {
    if (open) {
      loadPreview(previewRequest);
    }
  };

  return (
    <DocumentSheet onOpenChange={onOpenChange}>
      <DocumentSheetTrigger asChild>
        <Button variant="outline">PDF</Button>
      </DocumentSheetTrigger>
      <DocumentSheetContent className="max-h-[90vh]">
        <DocumentSheetHeader>
          <DocumentSheetTitle>{t("Download PDF")}</DocumentSheetTitle>
        </DocumentSheetHeader>
        <DocumentSheetMain>
          <DocumentSheetPreview
            objectURL={data?.objectURL}
            isLoading={isLoading}
            isFetching={isFetching}
            error={error}
          />
          <DocumentSheetActions>
            <DocumentSheetActionsHeader
              title={t("Auftragsbestätigung")}
              documentNumber={
                <ReferenceNumber
                  referenceNumber={confirmation.referenceNumber}
                />
              }
            />
            <DocumentSheetActionsContent>
              {!isImmutable(confirmation) && (
                <>
                  <DocumentNotReadyAlert document={t("Auftragsbestätigung")} />
                  <DocumentSheetImmutabilityWarning
                    title={t("Hinweis")}
                    description={t(
                      "Schreiben Sie die Auftragsbestätigung fest, um sie zu versenden.",
                    )}
                  />
                  <DocumentSheetActionsSetImmutableMutation
                    request={{
                      id: confirmation.id,
                      state: DocumentState.Immutable,
                      stateSetBy: user?.id || "",
                    }}
                    mutation={useRltConfirmationUpdateStateMutation}
                    description={t(
                      "Die Auftragsbestätigung wird festgeschrieben. Danach kann die Auftragsbestätigung nicht mehr geändert werden.",
                    )}
                    onSuccess={() => loadPreview(previewRequest)}
                    onError={(err) => write(err)}
                  />
                  <Separator />
                </>
              )}
              <DocumentSheetActionsDownload>
                <DownloadDocumentButton
                  confirmationId={confirmation.id}
                  document={ConfirmationDocument.All}
                  text={t("Alle Dokumente")}
                />
                <DownloadDocumentButton
                  confirmationId={confirmation.id}
                  document={ConfirmationDocument.Letter}
                  text={t("Bestätigung")}
                />
                <DownloadDocumentButton
                  confirmationId={confirmation.id}
                  document={ConfirmationDocument.Treatment}
                  text={t("Leistungsverzeichnis")}
                />
                <DownloadDocumentButton
                  confirmationId={confirmation.id}
                  document={ConfirmationDocument.TreatmentByArea}
                  text={t("Bereichsaufteilung")}
                />
              </DocumentSheetActionsDownload>
            </DocumentSheetActionsContent>
          </DocumentSheetActions>
        </DocumentSheetMain>
      </DocumentSheetContent>
    </DocumentSheet>
  );
}

function DownloadDocumentButton({
  confirmationId,
  document: documentType,
  text,
}: {
  confirmationId: string;
  document: ConfirmationDocument;
  text: string;
}) {
  const [open, setOpen] = useState(false);
  const [download, { isLoading, error, data, reset }] =
    useRltConfirmationPdfDownloadMutation();

  useDownloadPdf(data, () => {
    setOpen(false);
    reset();
  });

  useWriteValidationError(error, {
    onWrite: () => {
      setOpen(false);
      reset();
    },
  });

  const doDownload = () => {
    if (isLoading) {
      return;
    }

    download({ id: confirmationId, document: documentType });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="justify-start">
          <Download className="mr-4 h-5 w-5" /> {text}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Auftragsbestätigung herunterladen")}</DialogTitle>
          <DialogDescription>
            {t(
              "Wenn eine Auftragsbestätigung heruntergeladen wird, wird sie festgeschrieben. Sie kann dann nicht mehr geändert werden.",
            )}
          </DialogDescription>
        </DialogHeader>
        <RTKQueryErrorAlert error={error} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button onClick={doDownload} disabled={isLoading}>
            {isLoading ? (
              <Download className="mr-4 h-5 w-5 animate-bounce" />
            ) : (
              <Download className="mr-4 h-5 w-5" />
            )}
            <span>{t("Herunterladen")}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
